import React from 'react';
import { SkewButton, SectionTitle, Card, GradientDivider } from '../components/UIComponents';
import { BookOpen, Heart, Award, Users, Star, Sparkles, Target, Crown } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_goalgeous-hub/artifacts/ad5dhro0_Untitled%20design.png";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1662549905044-e3f71c293989?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxncm91cCUyMGZpdG5lc3MlMjBjbGFzcyUyMHdvbWVuJTIwZGl2ZXJzZSUyMGVuZXJnZXRpYyUyMGd5bXxlbnwwfHx8fDE3NzA3NjM1OTV8MA&ixlib=rb-4.1.0&q=85",
  empowerment: "https://images.unsplash.com/photo-1616803824305-a07cfbc8ea60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxzdHJvbmclMjBhZnJpY2FuJTIwYW1lcmljYW4lMjB3b21hbiUyMGZpdG5lc3MlMjBneW0lMjB3b3Jrb3V0fGVufDB8fHx8MTc3MDc2MzU5MXww&ixlib=rb-4.1.0&q=85",
};

const programs = [
  {
    icon: Crown,
    title: "Queen Confidence",
    description: "A transformative 8-week program to unlock your inner queen and radiate confidence in every area of life.",
    price: "$197",
    duration: "8 weeks",
    highlights: ["Weekly live coaching", "Private community access", "Journaling exercises", "Mindset shifts"]
  },
  {
    icon: Heart,
    title: "Self-Love Journey",
    description: "Learn to love yourself unapologetically. This course rewires negative self-talk and builds unshakeable self-worth.",
    price: "$147",
    duration: "6 weeks",
    highlights: ["Daily affirmations", "Mirror work practices", "Boundary setting", "Self-care rituals"]
  },
  {
    icon: Target,
    title: "Goal Getter Masterclass",
    description: "Stop dreaming and start achieving. Strategic goal-setting and execution framework for ambitious women.",
    price: "$97",
    duration: "4 weeks",
    highlights: ["Goal framework", "Accountability system", "Progress tracking", "Vision board workshop"]
  }
];

const resources = [
  { title: "Confidence Affirmations Guide", type: "PDF", price: "$15" },
  { title: "Daily Journaling Prompts", type: "PDF", price: "$12" },
  { title: "Goal Setting Workbook", type: "PDF", price: "$20" },
  { title: "Self-Love Audio Series", type: "Audio", price: "$35" },
];

const merchandise = [
  { name: "GOALGEOUS University T-Shirts", price: "$32", type: "apparel" },
  { name: "GOALGEOUS University Hoodies", price: "$68", type: "apparel" },
  { name: "GOALGEOUS University Leggings", price: "$58", type: "apparel" },
  { name: "GOALGEOUS University Hats", price: "$30", type: "apparel" },
  { name: "GOALGEOUS Signature EdgeWrap", price: "$25", type: "lifestyle", description: "Beauty & lifestyle item for laying edges while representing the confidence movement" },
];

const pillars = [
  { icon: Sparkles, title: "Mindset", description: "Rewire your thoughts for success" },
  { icon: Heart, title: "Self-Love", description: "Embrace your authentic self" },
  { icon: Target, title: "Goals", description: "Achieve what you dream" },
  { icon: Users, title: "Community", description: "Grow together with your sisters" },
];

export const GoalgeousPage = () => {
  return (
    <div data-testid="goalgeous-page" className="bg-brand-black pt-20">
      {/* Hero Section */}
      <section data-testid="goalgeous-hero" className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.hero} 
            alt="GOALGEOUS University" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-black via-brand-black/90 to-brand-black/50" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <p className="font-oswald text-sm uppercase tracking-[0.3em] text-brand-pink mb-4">
              Education & Growth
            </p>
            <h1 className="font-oswald text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white mb-6">
              GOALGEOUS<br />
              <span className="text-brand-pink">University</span>
            </h1>
            <GradientDivider />
            <p className="font-montserrat text-lg text-brand-gray leading-relaxed mt-8 mb-8">
              Where confident women are made. GOALGEOUS University is your destination for personal development, confidence coaching, and becoming the most powerful version of yourself.
            </p>
            <SkewButton variant="secondary" href="https://viagro.com" testId="goalgeous-hero-cta">
              Enroll Now
            </SkewButton>
          </div>
        </div>
      </section>

      {/* Pillars Section */}
      <section data-testid="pillars-section" className="py-24 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <SectionTitle 
            title="The Four Pillars" 
            subtitle="Our Foundation"
            accent="pink"
            align="center"
            className="mb-16"
          />
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {pillars.map((pillar, index) => (
              <div 
                key={pillar.title}
                data-testid={`pillar-${pillar.title.toLowerCase()}`}
                className="text-center group"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-pink/10 flex items-center justify-center group-hover:bg-brand-pink/20 transition-colors">
                  <pillar.icon size={32} className="text-brand-pink" />
                </div>
                <h3 className="font-oswald text-xl uppercase tracking-wide text-white mb-2">{pillar.title}</h3>
                <p className="font-montserrat text-sm text-brand-gray">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section data-testid="goalgeous-programs" className="py-24 bg-brand-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <SectionTitle 
            title="Signature Programs" 
            subtitle="Transform Your Life"
            className="mb-16"
          />
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <Card 
                key={program.title}
                testId={`goalgeous-program-${index}`}
                className="p-8 flex flex-col"
              >
                <div className="w-14 h-14 rounded-lg bg-brand-pink/10 flex items-center justify-center mb-6">
                  <program.icon size={28} className="text-brand-pink" />
                </div>
                <h3 className="font-oswald text-2xl uppercase tracking-tight text-white mb-3">
                  {program.title}
                </h3>
                <p className="font-montserrat text-sm text-brand-gray leading-relaxed mb-4 flex-grow">
                  {program.description}
                </p>
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-oswald text-2xl text-brand-pink">{program.price}</span>
                  <span className="text-sm text-brand-gray">• {program.duration}</span>
                </div>
                <ul className="space-y-2 mb-8">
                  {program.highlights.map((highlight, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-brand-gray">
                      <Star size={12} className="text-brand-pink" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
                <SkewButton 
                  variant="secondary" 
                  href="https://viagro.com"
                  testId={`enroll-program-${index}`}
                >
                  Enroll Now
                </SkewButton>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section data-testid="resources-section" className="py-24 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <SectionTitle 
            title="Digital Resources" 
            subtitle="Learn & Grow"
            accent="pink"
            className="mb-16"
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {resources.map((resource, index) => (
              <Card 
                key={resource.title}
                testId={`resource-${index}`}
                className="p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen size={16} className="text-brand-pink" />
                  <span className="font-montserrat text-xs uppercase tracking-wider text-brand-gray">{resource.type}</span>
                </div>
                <h4 className="font-oswald text-lg uppercase text-white mb-4">
                  {resource.title}
                </h4>
                <div className="flex items-center justify-between">
                  <span className="font-oswald text-xl text-brand-pink">{resource.price}</span>
                  <SkewButton 
                    variant="outline" 
                    href="https://viagro.com"
                    testId={`buy-resource-${index}`}
                    icon={false}
                    className="text-xs py-2 px-4"
                  >
                    Get
                  </SkewButton>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section data-testid="quote-section" className="py-24 bg-brand-black relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-20">
          <img 
            src={IMAGES.empowerment} 
            alt="Empowerment" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-brand-black" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="border-l-4 border-brand-pink pl-8">
            <p className="font-oswald text-3xl md:text-4xl text-white leading-relaxed mb-6">
              "You are not just setting goals. You are becoming <span className="text-brand-pink">GOALGEOUS</span>."
            </p>
            <p className="font-montserrat text-brand-gray">
              - The GOALGEOUS Philosophy
            </p>
          </div>
        </div>
      </section>

      {/* Merchandise Section */}
      <section data-testid="goalgeous-merch" className="py-24 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-16">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8">
              <SectionTitle 
                title="GOALGEOUS University Merch" 
                subtitle="Wear Your Growth"
                accent="pink"
              />
              <SkewButton variant="secondary" href="https://viagro.com" testId="shop-goalgeous-merch">
                Shop All
              </SkewButton>
            </div>
            <div className="max-w-3xl">
              <p className="font-oswald text-xl text-white mb-2">Represent the Mindset. Wear the Growth.</p>
              <p className="font-montserrat text-brand-gray leading-relaxed">
                GOALGEOUS University apparel is designed for women committed to confidence, personal development, and becoming the most powerful version of themselves.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
            {merchandise.map((item, index) => (
              <Card 
                key={item.name}
                testId={`goalgeous-merch-${index}`}
                className="p-6 text-center group"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-pink/10 flex items-center justify-center group-hover:bg-brand-pink/20 transition-colors">
                  {item.type === 'lifestyle' ? (
                    <Sparkles size={28} className="text-brand-pink" />
                  ) : (
                    <Crown size={28} className="text-brand-pink" />
                  )}
                </div>
                <h4 className="font-oswald text-xs uppercase text-white mb-1 leading-tight">{item.name}</h4>
                {item.description && (
                  <p className="font-montserrat text-xs text-brand-gray mt-2 mb-2">{item.description}</p>
                )}
                <span className="font-montserrat text-brand-pink font-semibold text-sm">{item.price}</span>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="goalgeous-cta" className="py-24 bg-brand-black">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <img 
            src={LOGO_URL} 
            alt="GOALGEOUS" 
            className="h-20 mx-auto mb-8"
          />
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase text-white mb-6">
            Become <span className="text-brand-pink">GOALGEOUS</span>
          </h2>
          <p className="font-montserrat text-lg text-brand-gray mb-8">
            Your transformation into the most confident, powerful version of yourself starts here.
          </p>
          <SkewButton variant="secondary" href="https://viagro.com" testId="goalgeous-final-cta">
            Join GOALGEOUS University
          </SkewButton>
        </div>
      </section>
    </div>
  );
};
